import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { linkedArtefactsApi, raciApi } from '../services/api';
import { useToast } from './useToast';

/**
 * "Sync from Controls": copy every RACI assignment from all linked Controls onto this artefact
 * (union, de-duplicated on person+role, existing rows skipped server-side by /raci/batch).
 *
 * Lifted verbatim from RaciAssignments.tsx's handleSyncFromControls so a page can offer the same
 * action from its own card head without mounting the full RaciAssignments editor.
 * RaciAssignments keeps its own copy; consolidating it onto this hook is a behaviour-preserving
 * follow-up, not part of the change that introduced this file.
 */
export function useSyncRaciFromControls(artefactId: string | undefined, artefactType: string) {
  const [isSyncing, setIsSyncing] = useState(false);
  const queryClient = useQueryClient();
  const toast = useToast();

  const sync = async () => {
    if (!artefactId) return;
    setIsSyncing(true);
    try {
      const linksData = await linkedArtefactsApi.getLinks(artefactId, artefactType);
      const links: any[] = linksData?.links || [];
      const controlLinks = links.filter((l: any) => l.artefact_type === 'control');

      if (controlLinks.length === 0) {
        toast.warning('No linked controls found to sync RACI from.');
        return;
      }

      const controlRaciResults = await Promise.all(
        controlLinks.map((l: any) => raciApi.getByArtefact('control', l.artefact_id))
      );

      const seenKeys = new Set<string>();
      const toCreate: Array<{ artefactType: string; artefactId: string; personId: string; role: string }> = [];
      for (const raciList of controlRaciResults) {
        for (const r of raciList) {
          const k = `${r.personId}:${r.role}`;
          if (!seenKeys.has(k)) {
            seenKeys.add(k);
            toCreate.push({ artefactType, artefactId, personId: r.personId, role: r.role });
          }
        }
      }

      if (toCreate.length === 0) {
        toast.info('Linked controls have no RACI assignments to sync.');
        return;
      }

      const result = await raciApi.batchCreate(toCreate);
      queryClient.invalidateQueries({ queryKey: ['raci', artefactType, artefactId] });
      const { created, skipped } = result as any;
      if (created === 0) {
        toast.info('All RACI already up to date.');
      } else {
        toast.success(`Synced ${created} assignment${created !== 1 ? 's' : ''} from linked controls${skipped > 0 ? ` (${skipped} already existed)` : ''}.`);
      }
    } catch {
      toast.error('Failed to sync RACI from controls.');
    } finally {
      setIsSyncing(false);
    }
  };

  return { sync, isSyncing };
}
